/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useEffect, useState, useRef, lazy, Suspense } from "react";
import { BrowserRouter as Router, Route, Routes, useLocation } from "react-router-dom";
import { Navigate } from "react-router";
import authManager from "./utils/authManager";
import LoginForm from "./pages/login/LoginForm";
import Home from "./pages/home";
import Restaurant from "./pages/restaurant";
import ReportHomePage from "./pages/reports/homepage";
import "./App.css";
import Layout from "./Layout";
import ModuleRoute from "./components/module_route";
import EmployeeDiscounts from "./pages/reports/employee_discounts";
import { Jobs } from "./pages/jobs";
import { JobPost } from "./pages/job_post";
import { UserManagement } from "./pages/user_management";
import { ResetPasswordFirstLogin } from "./pages/reset_password";
import { ForgotPassword } from "./pages/forgot_password";
import { ResetForgotPassword } from "./pages/reset_forgot_password";
import { DiscountCards } from "./pages/discount_cards";
import { NoPermissionPage } from "./pages/no_permissions";
import { RestaurantGroupPage } from "./pages/restaurant_groups";
import { CitiesPage } from "./pages/cities";
import { TipCompliance } from "./pages/reports/tip_compliance";
import { CheckInCompliance } from "./pages/reports/check_in_compliance";
import { BillingReport } from "./pages/reports/billing_report";
import { OwnersTableStaffRoute } from "./components/ownerstable_staff_route";
import { EmployeeDiscountCardSummary } from "./pages/reports/employee_discount_card_summary";
import { DetailedTransaction } from "./pages/reports/detailed_transaction";
import { Integrations } from "./pages/configurations/integrations";
import { PermissionProfiles } from "./pages/permission_profiles";
import { AuditLogsPage } from "./pages/audit_logs";
import { EmployeeVisibility } from "./pages/reports/employee_visibility";
import { UninvitedGuestPage } from "./pages/reports/uninvited_guest";
import EmployeeInsights from "./pages/reports/employee_insights";
import { DiscountCardLogPage } from "./pages/discount_card_logs/index"
import { EmployeeTimeEntries } from "./pages/reports/employee_time_entries";
import { GlobalEmployeeSearch } from "./pages/reports/global_employee_search";
import ErrorBoundaryWrapper from "./components/error_boundary/error_boundary_wrapper";
import useOnlineStatus from '../src/hooks/useOnlineStatus'
import { NamedUserDiscounts } from "./pages/reports/named_user_discounts";
import { ArchivedEmployees } from "./pages/reports/archived_employees";
import { Snackbar, Alert } from "@mui/material";
import { MobileUserActivityPage } from "./pages/reports/mobile_user_activity";
import { SESSION_KEYS } from "./lib/authConstants";
import { isAppEntryTarget, isEmailDeepLinkPath } from "./utils/loginRedirect";
import { EmployeeGroups } from "./pages/employee_groups";
import { GroupMembers } from "./pages/employee_groups/group_members";
import { DynamicDiscounts } from "./pages/dynamic_discounts";
import { ConfiguredDiscount } from "./pages/configured_discounts";
import { lazyWithRetry } from "./utils/lazyWithRetry";
import { GuestDetailedView } from "./pages/guest_details";
import { ClassifiedDiscountRecords } from "./pages/reports/classified_discount_records"
import { PerqReport } from "./pages/reports/perq_report"
import { LaborReport } from "./pages/reports/labor_report"
import { DailyLogbook } from "./pages/reports/daily_logbook"
import { DailySales } from "./pages/reports/daily_sales";
import { RevenueCenters } from "./pages/revenue_centers";
import { SalesCategories } from "./pages/sales_categories";
import { MealPeriods } from "./pages/meal_periods";
import { ServiceCharges } from "./pages/service_charges";
import { ImportLogPage } from "./pages/import_logs";
import { ImportLogDetailPage } from "./pages/import_log_detail";
import { PolicyEngine } from "./pages/policy_engine";
import { InactivityReview } from "./pages/policy_engine/inactivity_review";
import { InactivityConfirmation } from "./pages/inactivity_confirmation";
import { Loader } from "./components/loader";

const centeredLoaderSx = { minHeight: '70vh', display: 'flex', alignItems: 'center', justifyContent: 'center' };
const PageLoader = () => <Loader sx={centeredLoaderSx} />;

const Dashboard = lazy(lazyWithRetry(() => import('./pages/dashboard'), 'dashboard'))
const CreateRestaurant = lazy(lazyWithRetry(() => import('./pages/restaurant/create_restaurant'), 'create-restaurant'))
const UpdateRestaurant = lazy(lazyWithRetry(() => import('./pages/restaurant/update_restaurant'), 'update-restaurant'))
const Employee = lazy(lazyWithRetry(() => import('./pages/employee'), 'employee'))
const NetworkGuests = lazy(lazyWithRetry(() => import('./pages/network_guests'), 'network-guests'))

function App() {
  const [authState, setAuthState] = useState({
    isAuthenticated: authManager.isAuthenticated(),
  });
  const isOnline = useOnlineStatus();
  const previousStatus = useRef(isOnline);
  const [sessionExpired, setSessionExpired] = useState(false)
  const [onlineStatus, setOnlineStatus] = useState({
    open: false,
    status: '',
    message: ''
  })

  const handleSessionExpiredClose = (_event, reason) => {
    if (reason === 'clickaway') return;

    setSessionExpired(false);
  };

  useEffect(() => {
    const handleAuthStateChange = () => {
      const currentAuth = authManager.isAuthenticated();
      setAuthState({
        isAuthenticated: currentAuth,
      });

      if (currentAuth) setSessionExpired(false)
    };

    window.addEventListener('auth-state-changed', handleAuthStateChange);

    const handleStorageChange = (e) => {
      if (e.key === SESSION_KEYS.IS_AUTHENTICATED) {
        handleAuthStateChange();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    const handleSessionExpired = () => {
      setSessionExpired(true)
    };

    window.addEventListener("session-expired", handleSessionExpired);

    return () => {
      window.removeEventListener('auth-state-changed', handleAuthStateChange);
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('session-expired', handleSessionExpired);
    };
  }, []);

  useEffect(() => {
    if (previousStatus.current === true && isOnline === false) {
      setOnlineStatus({ ...onlineStatus, open: true, status: 'error', message: 'You have lost your internet connection' })
    }

    if (previousStatus.current === false && isOnline === true) {
      setOnlineStatus({ ...onlineStatus, open: true, status: 'success', message: 'Internet connection established' })
    }
    previousStatus.current = isOnline;
  }, [isOnline]);

  // Carries the destination through to the login screen only for an emailed deep
  // link the user opened the app with. Any other bounce to login — logging out of
  // a page, an expired session mid-session — drops it, so login lands on the
  // dashboard as it always did.
  const buildLoginRedirect = (location) => {
    const target = `${location.pathname}${location.search}`;

    if (!isEmailDeepLinkPath(location.pathname) || !isAppEntryTarget(target)) {
      return '/';
    }

    return `/?redirect_to=${encodeURIComponent(target)}`;
  };

  const PrivateRoute = ({ children }) => {
    const location = useLocation();
    const isCurrentlyAuth = authManager.isAuthenticated();
    const mustResetPassword = authManager.requiresPasswordReset();

    if (!isCurrentlyAuth) {
      return <Navigate to={buildLoginRedirect(location)} />;
    }

    if (mustResetPassword) {
      return <Navigate to={"/admin/reset_password"} replace />;
    }

    return (
      <Layout>
        <ModuleRoute>{children}</ModuleRoute>
      </Layout>
    );
  };

  const PublicRoute = ({ children }) => {
    const isCurrentlyAuth = authManager.isAuthenticated();
    const mustResetPassword = authManager.requiresPasswordReset();

    if (isCurrentlyAuth) {
      return <Navigate to={mustResetPassword ? "/admin/reset_password" : "/admin/dashboard"} replace />;
    }

    return children;
  };

  const PrivateRoutePages = ({ children }) => {
    const location = useLocation();
    const isCurrentlyAuth = authManager.isAuthenticated();
    const mustResetPassword = authManager.requiresPasswordReset();

    if (!isCurrentlyAuth) {
      return <Navigate to={buildLoginRedirect(location)} />;
    }

    if (mustResetPassword) {
      return <Navigate to={"/admin/reset_password"} replace />;
    }

    return isCurrentlyAuth ? (
      <React.Fragment>{children}</React.Fragment>
    ) : (
      <Navigate to={buildLoginRedirect(location)} />
    );
  };

  useEffect(() => {
    authManager.init();

    return () => {
      authManager.cleanup();
    };
  }, []);

  return (
    <React.Fragment>
      {/* Inactivity alert message */}
      <Snackbar
        open={sessionExpired}
        onClose={handleSessionExpiredClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        sx={{
          pointerEvents: "none",
          top: "40%",
        }}
      >
        <Alert
          variant="filled"
          severity="error"
          onClose={handleSessionExpiredClose}
          sx={{ pointerEvents: "auto" }}
        >
          Your session has expired due to inactivity, login again to continue
        </Alert>
      </Snackbar>

      {/* Internet connection alert message  */}
      <Snackbar
        open={onlineStatus?.open}
        onClose={() => setOnlineStatus({ ...onlineStatus, open: false, status: '', message: '' })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        sx={{
          pointerEvents: "none",
          top: "40%",
        }}
        autoHideDuration={4000}
      >
        <Alert
          variant="filled"
          severity={onlineStatus?.status}
          onClose={() => setOnlineStatus({ ...onlineStatus, open: false, status: '', message: '' })}
          sx={{ pointerEvents: "auto" }}
        >
          {onlineStatus?.message}
        </Alert>
      </Snackbar>

      {/* Routes for the pages */}
      <Router future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
        <Routes>
          <Route
            path="/"
            element={
              <PublicRoute>
                <ErrorBoundaryWrapper>
                  <LoginForm />
                </ErrorBoundaryWrapper>
              </PublicRoute>
            }
          />
          <Route
            path="/admin/home"
            element={
              <PrivateRoute>
                <Home />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/restaurant"
            element={
              <PrivateRoute>
                <Restaurant />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/restaurant/update/:id"
            element={
              <PrivateRoute>
                <ErrorBoundaryWrapper>
                  <Suspense fallback={<PageLoader />}>
                    <UpdateRestaurant />
                  </Suspense>
                </ErrorBoundaryWrapper>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/employees"
            element={
              <PrivateRoute>
                <Suspense fallback={<PageLoader />}>
                  <Employee />
                </Suspense>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/jobs"
            element={
              <PrivateRoute>
                <Jobs />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/revenue_centers"
            element={
              <PrivateRoute>
                <ErrorBoundaryWrapper>
                  <RevenueCenters />
                </ErrorBoundaryWrapper>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/sales_categories"
            element={
              <PrivateRoute>
                <ErrorBoundaryWrapper>
                  <SalesCategories />
                </ErrorBoundaryWrapper>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/meal_periods"
            element={
              <PrivateRoute>
                <ErrorBoundaryWrapper>
                  <MealPeriods />
                </ErrorBoundaryWrapper>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/service_charges"
            element={
              <PrivateRoute>
                <ErrorBoundaryWrapper>
                  <ServiceCharges />
                </ErrorBoundaryWrapper>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/jobs/:jobId"
            element={
              <PrivateRoute>
                <JobPost />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/reports/homepage"
            element={
              <PrivateRoute>
                <ErrorBoundaryWrapper>
                  <ReportHomePage />
                </ErrorBoundaryWrapper>
              </PrivateRoute>
            }
          />
          <Route path="/admin/reports/employee-discounts" element={<Navigate to="/admin/reports/employee-perqs" replace />} />
          <Route path="/admin/reports/employee-discount-report" element={<Navigate to="/admin/reports/employee-perq-report" replace />} />
          <Route path="/admin/reports/restaurant-discount-report" element={<Navigate to="/admin/reports/group-restaurant-perq-report" replace />} />
          <Route path="/admin/reports/restaurant-perq-report" element={<Navigate to="/admin/reports/group-restaurant-perq-report" replace />} />
          <Route path="/admin/reports/named_user_discounts" element={<Navigate to="/admin/reports/named_user_perqs" replace />} />
          <Route
            path="/admin/reports/employee-perqs"
            element={
              <PrivateRoute>
                <EmployeeDiscounts />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/reports/tip-compliance-report"
            element={
              <PrivateRoute>
                <TipCompliance />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/reports/check-in-compliance-report"
            element={
              <PrivateRoute>
                <ErrorBoundaryWrapper>
                  <CheckInCompliance />
                </ErrorBoundaryWrapper>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/reports/billing_report"
            element={
              <PrivateRoute>
                <ErrorBoundaryWrapper>
                  <OwnersTableStaffRoute>
                    <BillingReport />
                  </OwnersTableStaffRoute>
                </ErrorBoundaryWrapper>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/reports/employee-perq-report"
            element={
              <PrivateRoute>
                <ErrorBoundaryWrapper>
                  <EmployeeDiscountCardSummary />
                </ErrorBoundaryWrapper>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/reports/group-restaurant-perq-report"
            element={
              <PrivateRoute>
                <DetailedTransaction />
              </PrivateRoute>
            }
          />

          <Route
            path="/admin/user_management"
            element={
              <PrivateRoute>
                <ErrorBoundaryWrapper>
                  <UserManagement />
                </ErrorBoundaryWrapper>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/reset_password"
            element={<ResetPasswordFirstLogin />}
          />
          <Route path="/admin/forgot_password" element={<ForgotPassword />} />
          <Route
            path="/reset_forgot_password/:token"
            element={<ResetForgotPassword />}
          />
          <Route
            path="/admin/perq_cards"
            element={
              <PrivateRoute>
                <DiscountCards />
              </PrivateRoute>
            }
          />
          <Route path="/admin/discount_cards" element={<Navigate to="/admin/perq_cards" replace />} />
          <Route path="/admin/discount_cards/create" element={<Navigate to="/admin/perq_cards" replace />} />
          <Route path="/admin/discount_cards/edit/:id" element={<Navigate to="/admin/perq_cards" replace />} />
          <Route
            path="/admin/no_permissions"
            element={
              <PrivateRoutePages>
                <NoPermissionPage />
              </PrivateRoutePages>
            }
          />
          <Route
            path="/admin/groups"
            element={
              <PrivateRoute>
                <RestaurantGroupPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/cities"
            element={
              <PrivateRoute>
                <ErrorBoundaryWrapper>
                  <CitiesPage />
                </ErrorBoundaryWrapper>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/configurations/integrations"
            element={
              <PrivateRoute>
                <Integrations />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/permission_profiles"
            element={
              <PrivateRoute>
                <PermissionProfiles />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/audit_logs"
            element={
              <PrivateRoute>
                <ErrorBoundaryWrapper>
                  <AuditLogsPage />
                </ErrorBoundaryWrapper>
              </PrivateRoute>
            }
          />
          <Route path="/admin/discount_card_logs" element={<Navigate to="/admin/perq_card_logs" replace />} />
          <Route
            path="/admin/network_guests"
            element={
              <PrivateRoute>
                <ErrorBoundaryWrapper>
                  <Suspense fallback={<PageLoader />}>
                    <NetworkGuests />
                  </Suspense>
                </ErrorBoundaryWrapper>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/reports/employee_visibility"
            element={
              <PrivateRoute>
                <EmployeeVisibility />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/restaurant/create"
            element={
              <PrivateRoute>
                <ErrorBoundaryWrapper>
                  <Suspense fallback={<PageLoader />}>
                    <CreateRestaurant />
                  </Suspense>
                </ErrorBoundaryWrapper>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/reports/uninvited_guests"
            element={
              <PrivateRoute>
                <UninvitedGuestPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/reports/employee_insights"
            element={
              <PrivateRoute>
                <EmployeeInsights />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/reports/employee_time_entries"
            element={
              <PrivateRoute>
                <ErrorBoundaryWrapper>
                  <EmployeeTimeEntries />
                </ErrorBoundaryWrapper>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/reports/global_search"
            element={
              <PrivateRoute>
                <GlobalEmployeeSearch />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/reports/named_user_perqs"
            element={
              <PrivateRoute>
                <NamedUserDiscounts />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/reports/classified_discount_records"
            element={
              <PrivateRoute>
                <ClassifiedDiscountRecords />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/reports/perq_report"
            element={
              <PrivateRoute>
                <PerqReport />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/reports/labor_report"
            element={
              <PrivateRoute>
                <LaborReport />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/reports/daily_sales"
            element={
              <PrivateRoute>
                <DailySales />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/reports/daily_logbook"
            element={
              <PrivateRoute>
                <DailyLogbook />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/reports/archived_employees"
            element={
              <PrivateRoute>
                <ArchivedEmployees />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/perq_card_logs"
            element={
              <PrivateRoute>
                <DiscountCardLogPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/reports/mobile_user_activity"
            element={
              <PrivateRoute>
                <ErrorBoundaryWrapper>
                  <MobileUserActivityPage />
                </ErrorBoundaryWrapper>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/dashboard"
            element={
              <PrivateRoute>
                <ErrorBoundaryWrapper>
                  <Suspense fallback={<PageLoader />}>
                    <Dashboard />
                  </Suspense>
                </ErrorBoundaryWrapper>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/employee_groups"
            element={
              <PrivateRoute>
                <EmployeeGroups />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/employee_groups/:id/group_members"
            element={
              <PrivateRoute>
                <GroupMembers />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/dynamic_perqs"
            element={
              <PrivateRoute>
                <ErrorBoundaryWrapper>
                  <DynamicDiscounts />
                </ErrorBoundaryWrapper>
              </PrivateRoute>
            }
          />
          <Route path="/admin/dynamic_discounts" element={<Navigate to="/admin/dynamic_perqs" replace />} />
          <Route
            path="/admin/configured_perqs"
            element={
              <PrivateRoute>
                <ErrorBoundaryWrapper>
                  <ConfiguredDiscount />
                </ErrorBoundaryWrapper>
              </PrivateRoute>
            }
          />
          <Route path="/admin/configured_discounts" element={<Navigate to="/admin/configured_perqs" replace />} />
          <Route
            path="/admin/guest_details/:id"
            element={
              <PrivateRoute>
                <GuestDetailedView />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/import_logs"
            element={
              <PrivateRoute>
                <ImportLogPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/import_logs/:id"
            element={
              <PrivateRoute>
                <ImportLogDetailPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/policy_engine"
            element={
              <PrivateRoute>
                <PolicyEngine />
              </PrivateRoute>
            }
          />
          {/* Reached from the digest email and rendered without Layout, so it
              carries its own navbar — PrivateRoutePages authenticates without
              wrapping the admin nav around it. */}
          <Route
            path="/admin/policy_engine/inactivity_review"
            element={
              <PrivateRoutePages>
                <ErrorBoundaryWrapper>
                  <InactivityReview />
                </ErrorBoundaryWrapper>
              </PrivateRoutePages>
            }
          />
          {/* Intentionally unwrapped: the emailed token is the only credential, so
              PrivateRoute would bounce every guest and PublicRoute would bounce any
              admin with a live session in that browser. */}
          <Route
            path="/inactivity_confirmation"
            element={
              <ErrorBoundaryWrapper>
                <InactivityConfirmation />
              </ErrorBoundaryWrapper>
            }
          />
        </Routes>
      </Router>
    </React.Fragment>
  );
}

export default App;
